import { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Button, Form, Spinner } from 'react-bootstrap';
import { cerrarAperturaPlan, obtenerAperturaPlan, registrarAperturaPlan } from '../../services/aperturaPlanService';
import { subirArchivo } from '../../services/archivoService';
import { apellidoNombre } from '../../utils/nombrePersona';
import EditorPeriodos24476, { aFechaPeriodo, aMesPeriodo, mesAnio, periodoValido } from '../../components/PlanPago/EditorPeriodos24476';
import { CampoDato, formatearFecha, formatearHorario } from './atencionUi';
import '../../style/pages/VistaDetalladaAtencion.css';

const fechaLarga = (iso) => (iso ? formatearFecha(String(iso).slice(0, 10)) : '');

function Opcion({ valor, actual, onChange, children, disabled }) {
    const activo = valor === actual;
    return (
        <button
            type="button"
            className={`atn-tab${activo ? ' atn-tab--activa' : ''}`}
            aria-pressed={activo}
            disabled={disabled}
            onClick={() => onChange(valor)}
        >
            {children}
        </button>
    );
}

function SiNo({ etiqueta, valor, onChange, disabled, ayuda }) {
    return (
        <div className="mb-3">
            <div className="atn-form-label mb-1">{etiqueta}</div>
            <div className="atn-tabs" role="group" aria-label={etiqueta}>
                <Opcion valor={true} actual={valor} onChange={onChange} disabled={disabled}>Sí</Opcion>
                <Opcion valor={false} actual={valor} onChange={onChange} disabled={disabled}>No</Opcion>
            </div>
            {ayuda && <div className="atn-form-hint">{ayuda}</div>}
        </div>
    );
}

/**
 * Ficha "Apertura del plan" (15-oct-2026): el cliente volvió de ANSES (reingreso APERTURA_PLAN) y
 * quien lo llama registra lo que se hizo. Muestra lo previsto en el plan y deja cargar lo hecho,
 * que puede cambiar en el momento. Al finalizar se cierran este turno y el turno ANSES de origen.
 */
export default function VistaDetalladaAtencionAperturaPlan({
    turno, estadoActual, guardando, onEmpezarAtencion, onError, onFinalizada, onCrearJubilacion, recarga = 0,
}) {
    const [datos, setDatos] = useState(null);
    const [cargando, setCargando] = useState(true);
    const [enviando, setEnviando] = useState(false);
    const [subiendo, setSubiendo] = useState(false);
    const [adjuntos, setAdjuntos] = useState([]);
    const [form, setForm] = useState(null);

    const idTurno = turno?.idTurno;
    const enAtencion = estadoActual === 'EN_CURSO';

    const cargar = useCallback(async () => {
        if (!idTurno) return;
        setCargando(true);
        try {
            const d = await obtenerAperturaPlan(idTurno);
            setDatos(d);
            const plan = d.plan || {};
            setForm({
                abrioPlan: null,
                nroExpediente: d.presentacionNroExpediente || '',
                motivoNoAbrio: '',
                meses27705: plan.previstoMeses27705 ?? '',
                monto27705: '',
                mesesAComprarCliente: d.clienteMesesLey27705 ?? '',
                fechaCumpleEdad: d.clienteFechaCumpleEdad || '',
                compro24476: (plan.periodos24476Previstos || []).length > 0 ? true : null,
                periodos24476: (plan.periodos24476Previstos || []).map((p) => ({ desde: aMesPeriodo(p.desde), hasta: aMesPeriodo(p.hasta) })),
                inscribioMonotributo: plan.previstoMonotributo ?? null,
                observacion: '',
            });
        } catch (e) {
            onError?.(e?.response?.data?.mensaje || e?.response?.data?.message || 'No se pudo cargar la apertura del plan.');
        } finally {
            setCargando(false);
        }
        // onError viene del padre y cambia en cada render: no es una dependencia real.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [idTurno]);

    useEffect(() => { cargar(); }, [cargar]);

    // Se creó un trámite desde la ficha (la Jubilación): se refrescan los datos sin perder lo cargado.
    useEffect(() => {
        if (!recarga || !idTurno) return;
        obtenerAperturaPlan(idTurno).then(setDatos).catch(() => {});
    }, [recarga, idTurno]);

    const plan = datos?.plan || {};
    const nombre = apellidoNombre(turno?.apellido, turno?.nombre) || 'Cliente';
    const faltaDatoCliente = datos && (datos.clienteMesesLey27705 == null || !datos.clienteFechaCumpleEdad);

    const puedeFinalizar = useMemo(() => {
        if (!form || form.abrioPlan === null) return false;
        if (form.abrioPlan && !form.nroExpediente.trim()) return false;
        if (!form.abrioPlan && !form.motivoNoAbrio.trim()) return false;
        if (form.compro24476 && (!form.periodos24476.length || form.periodos24476.some((p) => !periodoValido(p)))) return false;
        return true;
    }, [form]);

    const subirAdjuntos = async (archivos) => {
        if (!archivos?.length || !datos?.idTramitePlan) return;
        setSubiendo(true);
        try {
            for (const archivo of archivos) {
                // eslint-disable-next-line no-await-in-loop
                await subirArchivo(datos.idTramitePlan, archivo);
                setAdjuntos((prev) => [...prev, archivo.name]);
            }
        } catch (e) {
            onError?.('No se pudo subir uno de los documentos.');
        } finally {
            setSubiendo(false);
        }
    };

    const finalizar = async () => {
        if (!puedeFinalizar) return;
        setEnviando(true);
        try {
            await registrarAperturaPlan(idTurno, {
                abrioPlan: form.abrioPlan,
                nroExpediente: form.nroExpediente.trim() || null,
                motivoNoAbrio: form.motivoNoAbrio.trim() || null,
                meses27705: form.meses27705 === '' ? null : Number(form.meses27705),
                monto27705: form.monto27705 === '' ? null : Number(form.monto27705),
                mesesAComprarCliente: datos.clienteMesesLey27705 == null && form.mesesAComprarCliente !== ''
                    ? Number(form.mesesAComprarCliente) : null,
                fechaCumpleEdad: !datos.clienteFechaCumpleEdad && form.fechaCumpleEdad ? form.fechaCumpleEdad : null,
                compro24476: form.compro24476 === true,
                periodos24476: form.compro24476 ? form.periodos24476.map((p) => ({ desde: aFechaPeriodo(p.desde), hasta: aFechaPeriodo(p.hasta) })) : [],
                inscribioMonotributo: form.inscribioMonotributo,
                observacion: form.observacion.trim() || null,
            });
            onFinalizada?.();
        } catch (e) {
            onError?.(e?.response?.data?.mensaje || e?.response?.data?.message || 'No se pudo registrar la apertura del plan.');
        } finally {
            setEnviando(false);
        }
    };

    // El plan ya se había abierto (otra apertura, o un turno que quedó en ANSES): no hay nada que
    // registrar, pero la atención tiene que poder terminar.
    const cerrarSinRegistrar = async () => {
        setEnviando(true);
        try {
            await cerrarAperturaPlan(idTurno);
            onFinalizada?.();
        } catch (e) {
            onError?.(e?.response?.data?.mensaje || e?.response?.data?.message || 'No se pudo cerrar el turno.');
        } finally {
            setEnviando(false);
        }
    };

    const set = (campo) => (valor) => setForm((f) => ({ ...f, [campo]: valor }));

    // ─────────────── Etapa 1: volvió de ANSES, todavía no lo llamaron ───────────────
    if (estadoActual === 'ASISTIO') {
        return (
            <div className="atn-shell">
                <div className="atn-card atn-header">
                    <div className="atn-header-top">
                        <div>
                            <h2 className="atn-title">Apertura del plan de pago</h2>
                            <p className="atn-subtitle">Volvió de ANSES: hay que abrir el plan de pago.</p>
                        </div>
                        <span className="atn-badge-etapa">Etapa 1</span>
                    </div>
                </div>
                <div className="atn-contenido">
                    <div className="atn-card">
                        <div className="atn-field-grid">
                            <CampoDato etiqueta="Cliente" valor={nombre} />
                            <CampoDato etiqueta="CUIL" valor={turno.cuil || turno.dni || 'Sin dato'} muted={!(turno.cuil || turno.dni)} />
                            <CampoDato etiqueta="Volvió" valor={`${formatearFecha(turno.fechaTurno)} - ${formatearHorario(turno.horario)}`} />
                        </div>
                    </div>
                    <div className="atn-actions">
                        <Button className="atn-btn-primary" onClick={onEmpezarAtencion} disabled={guardando}>
                            {guardando ? <Spinner animation="border" size="sm" className="me-2" /> : <i className="bi bi-play-fill me-2"></i>}
                            Empezar apertura
                        </Button>
                    </div>
                </div>
            </div>
        );
    }

    if (cargando || !form) {
        return (
            <div className="atn-shell">
                <div className="atn-card text-center"><Spinner animation="border" size="sm" className="me-2" />Cargando el plan…</div>
            </div>
        );
    }

    const soloLectura = !enAtencion || datos?.yaAbierto;
    const cuil = datos?.cuil;

    return (
        <div className="atn-shell">
            <div className="atn-card atn-header">
                <div className="atn-header-top">
                    <div>
                        <h2 className="atn-title">Apertura del plan de pago</h2>
                        <p className="atn-subtitle">
                            {soloLectura
                                ? 'Resumen de la apertura.'
                                : 'Arriba está lo previsto en el plan. Registrá lo que se hizo efectivamente.'}
                        </p>
                    </div>
                    <span className={`atn-badge-etapa${soloLectura ? ' atn-badge-etapa--success' : ''}`}>
                        {soloLectura ? 'Finalizado' : 'Etapa 2'}
                    </span>
                </div>
                <div className="atn-header-client">
                    <div className="atn-avatar-circle">{nombre.split(/[\s,]+/).filter(Boolean).slice(0, 2).map((p) => p[0]).join('').toUpperCase()}</div>
                    <div className="atn-header-body">
                        <div className="atn-header-name">{nombre}</div>
                        <div className="atn-header-meta"><span>CUIL {cuil || turno.cuil || 'Sin dato'}</span></div>
                    </div>
                    <div className="atn-header-actions d-flex flex-wrap gap-2">
                        {/* Accesos directos: al abrir el plan pueden aparecer aportes o hijos que no estaban
                            (por ejemplo, una AUH que ANSES no le reconoce). Se abren en otra pestaña. */}
                        {cuil && (
                            <a className="btn atn-btn-secondary btn-outline-secondary" href={`/herramientas/historial-laboral?cuil=${cuil}`}
                                target="_blank" rel="noopener noreferrer">
                                <i className="bi bi-calculator me-1" />Modificar aportes
                            </a>
                        )}
                        {cuil && (
                            <a className="btn atn-btn-secondary btn-outline-secondary" href={`/clientes/vista-detallada/${cuil}`}
                                target="_blank" rel="noopener noreferrer">
                                <i className="bi bi-people me-1" />Modificar hijos
                            </a>
                        )}
                        {datos?.idTramitePlan && (
                            <a className="btn atn-btn-secondary btn-outline-secondary" href={`/plan-pago/tramites/${datos.idTramitePlan}`}
                                target="_blank" rel="noopener noreferrer">
                                <i className="bi bi-box-arrow-up-right me-1" />Ver el plan
                            </a>
                        )}
                    </div>
                </div>
            </div>

            <div className="atn-contenido">
                {/* ── Lo previsto ── */}
                <div className="atn-card">
                    <h5 className="atn-section-title"><i className="bi bi-list-check me-2" />Lo previsto en el plan</h5>
                    <div className="atn-field-grid">
                        <CampoDato etiqueta="Monotributo" valor={plan.previstoMonotributo == null ? 'Sin definir' : (plan.previstoMonotributo ? 'Inscribirlo' : 'No inscribirlo')}
                            muted={plan.previstoMonotributo == null} />
                        <CampoDato etiqueta="Ley 27.705" valor={plan.previstoMeses27705 ? `${plan.previstoMeses27705} meses` : 'Sin definir'} muted={!plan.previstoMeses27705} />
                        <CampoDato etiqueta="Ley 24.476"
                            valor={(plan.periodos24476Previstos || []).length
                                ? plan.periodos24476Previstos.map((p) => `${mesAnio(p.desde)}–${mesAnio(p.hasta)}`).join(', ')
                                : 'Sin períodos'}
                            muted={!(plan.periodos24476Previstos || []).length} />
                    </div>
                    {plan.previstoObservacion && <p className="atn-text-secondary mt-2 mb-0">{plan.previstoObservacion}</p>}
                    {(datos.periodos24476Cliente || []).length > 0 && (
                        <p className="atn-text-secondary mt-2 mb-0">
                            Ya compró por la Ley 24.476: {datos.periodos24476Cliente.map((p) => `${mesAnio(p.desde)}–${mesAnio(p.hasta)}`).join(', ')}.
                        </p>
                    )}
                </div>

                {/* ── Jubilación ── */}
                <div className="atn-card">
                    <h5 className="atn-section-title"><i className="bi bi-person-vcard me-2" />Jubilación</h5>
                    {datos.idJubilacion ? (
                        <p className="mb-0">
                            Tiene trámite de Jubilación{datos.estadoJubilacion ? ` (${datos.estadoJubilacion})` : ''}.{' '}
                            <a href={`/jubilacion/tramites/${datos.idJubilacion}`} target="_blank" rel="noopener noreferrer">Ver</a>
                        </p>
                    ) : (
                        <Alert variant="warning" className="mb-0 d-flex flex-wrap align-items-center gap-2">
                            <span>
                                <i className="bi bi-exclamation-triangle-fill me-2" />
                                El cliente no tiene trámite de Jubilación.
                                {datos.clienteFechaCumpleEdad && ` Liquidar: ${mesAnio(datos.clienteFechaCumpleEdad)} (cuando cumple la edad).`}
                            </span>
                            {!soloLectura && (
                                <Button size="sm" className="atn-btn-primary ms-auto"
                                    onClick={() => onCrearJubilacion?.(form.fechaCumpleEdad || datos.clienteFechaCumpleEdad || null)}>
                                    <i className="bi bi-plus-lg me-1" />Crear trámite de Jubilación
                                </Button>
                            )}
                        </Alert>
                    )}
                </div>

                {/* ── Datos del cliente para el plan (si no estaban) ── */}
                {faltaDatoCliente && !soloLectura && (
                    <div className="atn-card">
                        <h5 className="atn-section-title"><i className="bi bi-person-lines-fill me-2" />Datos del cliente para el plan</h5>
                        <p className="atn-form-hint">No estaban cargados desde la calculadora de aportes. Se guardan en el cliente.</p>
                        <div className="d-flex flex-wrap gap-3">
                            {datos.clienteMesesLey27705 == null && (
                                <div>
                                    <label className="atn-form-label" htmlFor="ap-meses-cliente">Meses a comprar (Ley 27.705)</label>
                                    <Form.Control id="ap-meses-cliente" type="number" min="0" size="sm" value={form.mesesAComprarCliente}
                                        onChange={(e) => set('mesesAComprarCliente')(e.target.value)} />
                                </div>
                            )}
                            {!datos.clienteFechaCumpleEdad && (
                                <div>
                                    <label className="atn-form-label" htmlFor="ap-fecha-edad">Cumple la edad jubilatoria (fecha límite)</label>
                                    <Form.Control id="ap-fecha-edad" type="date" size="sm" value={form.fechaCumpleEdad}
                                        onChange={(e) => set('fechaCumpleEdad')(e.target.value)} />
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* ── Lo que se hizo ── */}
                {soloLectura ? (
                    <div className="atn-card">
                        <h5 className="atn-section-title"><i className="bi bi-check2-circle me-2" />Lo que se hizo</h5>
                        <div className="atn-field-grid">
                            <CampoDato etiqueta="Abierto" valor={plan.aperturaEn ? `${fechaLarga(plan.aperturaEn)}${plan.aperturaPor ? ` · ${plan.aperturaPor.split('@')[0]}` : ''}` : 'Sin registrar'} />
                            <CampoDato etiqueta="Expediente" valor={datos.presentacionNroExpediente || 'Sin dato'} muted={!datos.presentacionNroExpediente} />
                            <CampoDato etiqueta="Compras Ley 27.705" valor={(plan.compras || []).map((c) => `${c.cantidadMeses} meses`).join(', ') || 'Ninguna'} />
                        </div>
                        {plan.aperturaObservacion && <p className="atn-text-secondary mt-2 mb-0">{plan.aperturaObservacion}</p>}
                    </div>
                ) : (
                    <div className="atn-card">
                        <h5 className="atn-section-title"><i className="bi bi-pencil-square me-2" />Lo que se hizo</h5>

                        <SiNo etiqueta="¿Se abrió el plan de la Ley 27.705?" valor={form.abrioPlan} onChange={set('abrioPlan')} disabled={enviando} />
                        {form.abrioPlan === true && (
                            <div className="d-flex flex-wrap gap-3 mb-3">
                                <div style={{ minWidth: 240 }}>
                                    <label className="atn-form-label" htmlFor="ap-expediente">N° de expediente *</label>
                                    <Form.Control id="ap-expediente" size="sm" maxLength={60} value={form.nroExpediente}
                                        placeholder="024-20-12345678-9-…" onChange={(e) => set('nroExpediente')(e.target.value)} />
                                </div>
                                <div>
                                    <label className="atn-form-label" htmlFor="ap-meses">Meses que compró</label>
                                    <Form.Control id="ap-meses" type="number" min="0" size="sm" value={form.meses27705}
                                        onChange={(e) => set('meses27705')(e.target.value)} />
                                </div>
                                <div>
                                    <label className="atn-form-label" htmlFor="ap-monto">Monto (opcional)</label>
                                    <Form.Control id="ap-monto" type="number" min="0" step="0.01" size="sm" value={form.monto27705}
                                        onChange={(e) => set('monto27705')(e.target.value)} />
                                </div>
                                <div className="atn-form-hint w-100 mt-0">
                                    El plan ingresa en ANSES y pasa directo a Favorables.
                                </div>
                            </div>
                        )}
                        {form.abrioPlan === false && (
                            <div className="mb-3">
                                <label className="atn-form-label" htmlFor="ap-motivo">¿Por qué no se pudo abrir? *</label>
                                <Form.Control id="ap-motivo" as="textarea" rows={2} className="atn-textarea" value={form.motivoNoAbrio}
                                    onChange={(e) => set('motivoNoAbrio')(e.target.value)} />
                            </div>
                        )}

                        <div className="atn-field-divider" />
                        <SiNo etiqueta="¿Compró períodos por la Ley 24.476?" valor={form.compro24476} onChange={set('compro24476')} disabled={enviando}
                            ayuda="Quedan en el cliente: son sus aportes, sirven también para la jubilación." />
                        {form.compro24476 && (
                            <div className="mb-3">
                                <EditorPeriodos24476 periodos={form.periodos24476} onChange={set('periodos24476')} disabled={enviando} idBase="ap-24476" />
                            </div>
                        )}

                        <div className="atn-field-divider" />
                        <SiNo etiqueta="¿Se hizo la inscripción al monotributo?" valor={form.inscribioMonotributo} onChange={set('inscribioMonotributo')} disabled={enviando} />

                        <div className="atn-field-divider" />
                        <div className="mb-3">
                            <label className="atn-form-label" htmlFor="ap-adjuntos">Documentos</label>
                            <Form.Control id="ap-adjuntos" type="file" multiple size="sm" disabled={subiendo || enviando}
                                onChange={(e) => { subirAdjuntos(Array.from(e.target.files || [])); e.target.value = ''; }} />
                            <div className="atn-form-hint">Se guardan en la documentación del plan de pago.</div>
                            {subiendo && <div className="atn-form-hint"><Spinner animation="border" size="sm" className="me-1" />Subiendo…</div>}
                            {adjuntos.length > 0 && (
                                <ul className="mb-0 mt-1 small">{adjuntos.map((n, i) => <li key={`${n}-${i}`}>{n}</li>)}</ul>
                            )}
                        </div>
                        <div>
                            <label className="atn-form-label" htmlFor="ap-observacion">Observación</label>
                            <Form.Control id="ap-observacion" as="textarea" rows={2} className="atn-textarea" value={form.observacion}
                                onChange={(e) => set('observacion')(e.target.value)} />
                        </div>
                    </div>
                )}

                {soloLectura && enAtencion && datos?.yaAbierto && (
                    <div className="atn-actions">
                        <span className="atn-text-secondary me-auto">
                            <i className="bi bi-info-circle me-1" />
                            Este plan ya estaba abierto: no hay otra apertura que registrar.
                        </span>
                        <Button className="atn-btn-primary" onClick={cerrarSinRegistrar} disabled={enviando}>
                            {enviando ? <Spinner animation="border" size="sm" className="me-2" /> : <i className="bi bi-check2 me-2" />}
                            Cerrar turno
                        </Button>
                    </div>
                )}

                {!soloLectura && (
                    <div className="atn-actions">
                        <Button className="atn-btn-primary" onClick={finalizar} disabled={!puedeFinalizar || enviando || subiendo}>
                            {enviando ? <Spinner animation="border" size="sm" className="me-2" /> : <i className="bi bi-check2-all me-2" />}
                            Finalizar apertura
                        </Button>
                    </div>
                )}
            </div>
        </div>
    );
}
